/**
 * /settings/custom-fields — list + create + delete custom field defs.
 * Only `label` and `description` are editable post-creation; `name`
 * and `type` are immutable (would silently invalidate stored values).
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface FieldRow {
  id: string;
  name: string;
  label: string;
  type: "text" | "number" | "date" | "bool";
  description: string | null;
  createdAt: string;
}

export const Route = createFileRoute("/_app/settings/custom-fields")({
  component: CustomFieldsPage,
});

function CustomFieldsPage() {
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [type, setType] = useState<FieldRow["type"]>("text");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ fields: FieldRow[] }>("/custom-fields");
      setFields(r.data.fields);
    } catch {
      setError("Failed to load fields");
    } finally {
      setLoading(false);
    }
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !label.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/custom-fields", {
        name: name.trim(),
        label: label.trim(),
        type,
        description: description.trim() || null,
      });
      setName("");
      setLabel("");
      setType("text");
      setDescription("");
      await load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg ?? "Failed to create field");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string, fieldName: string) {
    if (!confirm(`Delete custom field "${fieldName}"? Existing values on contacts are preserved but will become orphaned.`)) return;
    try {
      await api.delete(`/custom-fields/${id}`);
      setFields((cur) => cur.filter((f) => f.id !== id));
    } catch {
      setError("Failed to delete");
    }
  }

  return (
    <section style={{ maxWidth: 720, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>Custom fields</h1>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Per-contact data slots. Use them in flows via <code>set_field</code> +
        reference in messages with <code>{`{{name}}`}</code>.
      </p>

      <form
        onSubmit={create}
        className="brut-card"
        style={{ marginBottom: 24, display: "grid", gap: 10 }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>New field</div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr 140px" }}>
          <div>
            <label className="brut-label">Name (identifier)</label>
            <input
              placeholder="lifetime_value"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="brut-input"
              maxLength={64}
              required
              pattern="[a-zA-Z_][a-zA-Z0-9_]*"
            />
          </div>
          <div>
            <label className="brut-label">Label (display)</label>
            <input
              placeholder="Lifetime value"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="brut-input"
              maxLength={120}
              required
            />
          </div>
          <div>
            <label className="brut-label">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as FieldRow["type"])}
              className="brut-input"
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
              <option value="bool">Bool</option>
            </select>
          </div>
        </div>
        <input
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="brut-input"
          maxLength={500}
        />
        <div>
          <button type="submit" disabled={!name.trim() || !label.trim() || busy} className="brut-button">
            {busy ? "Adding…" : "Add field"}
          </button>
        </div>
      </form>

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {loading ? (
        <p>Loading…</p>
      ) : fields.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 24 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 700 }}>No custom fields yet</p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            Custom fields capture per-contact data — email, signup source,
            order count. Add one above ↑ to use it from{" "}
            <code>set_field</code> nodes in flows or as a segment filter.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {fields.map((f) => (
            <li key={f.id} className="brut-card" style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {f.label} <code style={{ color: "var(--muted)", fontWeight: 400, fontSize: 12 }}>{f.name}</code>
                </div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>type: {f.type}</div>
                {f.description ? <div style={{ fontSize: 13, marginTop: 4 }}>{f.description}</div> : null}
              </div>
              <button
                onClick={() => void remove(f.id, f.name)}
                className="brut-button is-danger"
                style={{ fontSize: 13, padding: "6px 10px" }}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
